import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createAdminApiClient } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Package, FolderTree, MessageSquareQuote, ArrowRight, AlertCircle, ShoppingBag, FileText } from "lucide-react";

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const credStr = sessionStorage.getItem('adminCredentials');
                if (!credStr) {
                    setLoading(false);
                    return;
                }
                const credentials = JSON.parse(credStr);
                const adminApi = createAdminApiClient(credentials.username, credentials.password);
                const res = await adminApi.getStats();
                setStats(res.data);
            } catch (err) {
                console.error("Failed to fetch stats:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);
    
    const statCards = [
        {
            label: "Products",
            value: stats?.products || 0,
            icon: Package,
            href: "/admin/products",
            color: "text-blue-500"
        },
        {
            label: "Categories",
            value: stats?.categories || 0,
            icon: FolderTree,
            href: "/admin/categories",
            color: "text-green-500"
        },
        {
            label: "Total Quotes",
            value: stats?.total_quotes || 0,
            icon: MessageSquareQuote,
            href: "/admin/quotes",
            color: "text-primary"
        },
        {
            label: "New Quotes",
            value: stats?.new_quotes || 0,
            icon: AlertCircle,
            href: "/admin/quotes?status=new",
            color: "text-yellow-500",
            highlight: true
        },
        {
            label: "Orders",
            value: stats?.orders || 0,
            icon: ShoppingBag,
            href: "/admin/orders",
            color: "text-purple-500"
        },
        {
            label: "Blog Posts",
            value: stats?.blog_posts || 0,
            icon: FileText,
            href: "/admin/blog",
            color: "text-cyan-500"
        },
    ];
    
    return (
        <div data-testid="admin-dashboard">
            <div className="mb-8">
                <h1 className="font-heading font-black text-3xl tracking-tight mb-2">Dashboard</h1>
                <p className="text-muted-foreground">Welcome to the Dryice Marketplace CMS</p>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {statCards.map((stat) => (
                    <Link key={stat.label} to={stat.href}>
                        <Card className={`bg-card border border-border rounded-none hover:border-primary/50 transition-colors ${stat.highlight ? 'border-primary/30' : ''}`}>
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <stat.icon className={`h-8 w-8 ${stat.color}`} />
                                    {stat.highlight && stat.value > 0 && (
                                        <span className="bg-primary text-primary-foreground text-xs font-bold px-2 py-1">
                                            NEW
                                        </span>
                                    )}
                                </div>
                                <p className="font-mono text-3xl font-bold mb-1">
                                    {loading ? "—" : stat.value}
                                </p>
                                <p className="text-muted-foreground text-sm">{stat.label}</p>
                            </CardContent>
                        </Card>
                    </Link>
                ))}
            </div>
            
            {/* Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-card border border-border rounded-none">
                    <CardContent className="p-6">
                        <h3 className="font-heading font-bold text-lg mb-4">Quick Actions</h3>
                        <div className="space-y-2">
                            <Link 
                                to="/admin/products" 
                                className="flex items-center justify-between p-3 bg-secondary/50 hover:bg-secondary transition-colors"
                            >
                                <span>Manage Products</span>
                                <ArrowRight className="h-4 w-4 text-primary" />
                            </Link>
                            <Link 
                                to="/admin/categories" 
                                className="flex items-center justify-between p-3 bg-secondary/50 hover:bg-secondary transition-colors"
                            >
                                <span>Manage Categories</span>
                                <ArrowRight className="h-4 w-4 text-primary" />
                            </Link>
                            <Link 
                                to="/admin/quotes" 
                                className="flex items-center justify-between p-3 bg-secondary/50 hover:bg-secondary transition-colors"
                            >
                                <span>View Quote Requests</span>
                                <ArrowRight className="h-4 w-4 text-primary" />
                            </Link>
                            <Link 
                                to="/admin/comparisons" 
                                className="flex items-center justify-between p-3 bg-secondary/50 hover:bg-secondary transition-colors"
                            >
                                <span>Edit Comparison Tables</span>
                                <ArrowRight className="h-4 w-4 text-primary" />
                            </Link>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="bg-card border border-border rounded-none">
                    <CardContent className="p-6">
                        <h3 className="font-heading font-bold text-lg mb-4">CMS Overview</h3>
                        <div className="space-y-4 text-sm text-muted-foreground">
                            <p>
                                <strong className="text-foreground">Products:</strong> Add and manage equipment listings with full specifications, images, and pricing.
                            </p>
                            <p>
                                <strong className="text-foreground">Categories:</strong> Organize products by application type. Products can belong to multiple categories.
                            </p>
                            <p>
                                <strong className="text-foreground">Quote Requests:</strong> View and manage incoming quote and guidance requests from customers.
                            </p>
                            <p>
                                <strong className="text-foreground">Comparison Tables:</strong> Create and edit product comparison tables for the public site.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AdminDashboard;
